import { useEffect, useRef, useState } from "react";
import { apiClient, API } from "@/context/AuthContext";
import { getPosition, mapsUrl, uploadGeoEvidence } from "@/lib/geo";
import { Camera, Check, Loader2, MapPin, Search, X } from "lucide-react";

const VERDICTS = [
  { action: "confirm", label: "Uygun", cls: "ok", icon: Check },
  { action: "reject", label: "Uygunsuz", cls: "bad", icon: X },
  { action: "correct", label: "Sahada doğrulanmalı", cls: "warn", icon: Search },
];

export default function FieldFindingSheet({ finding, assessmentId, onClose, onChanged, onToast }) {
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const inputRef = useRef();

  useEffect(() => { (async () => {
    try { const { data } = await apiClient.get(`/findings/${finding.id}/evidence`); setShots(data || []); } catch { /* ignore */ }
  })(); }, [finding.id]);

  const capture = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const geo = await getPosition();
      const row = await uploadGeoEvidence(finding.id, file, geo, note);
      setShots((s) => [row, ...s]);
      onToast(geo ? "Kanıt GPS damgasıyla kaydedildi" : "Kanıt kaydedildi · GPS alınamadı");
      onChanged();
    } catch (err) {
      onToast(err?.response?.data?.detail || "Fotoğraf yüklenemedi");
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const verify = async (action) => {
    try {
      await apiClient.post(`/assessments/${assessmentId}/findings/${finding.id}/verify`, { action, note });
      onToast("Saha kararı kaydedildi");
      onChanged();
      onClose();
    } catch (err) { onToast(err?.response?.data?.detail || "Karar kaydedilemedi"); }
  };

  return (
    <div className="field-sheet-backdrop" onClick={onClose} data-testid="field-sheet">
      <div className="field-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="field-sheet-grip"/>
        <div className="field-sheet-head">
          <div><div className="eyebrow">{finding.source}</div><h3>{finding.title}</h3></div>
          <button className="icon-button" onClick={onClose} data-testid="field-sheet-close"><X size={18}/></button>
        </div>
        <p className="field-action">{finding.action_text}</p>
        {finding.question && <small className="field-question">{finding.question}</small>}
        <label className={`field-camera ${busy ? "busy" : ""}`} data-testid="field-camera-button">
          {busy ? <Loader2 size={26} className="spin"/> : <Camera size={26}/>}
          <span>{busy ? "Konum alınıyor ve yükleniyor…" : "Fotoğraf çek"}</span>
          <small>Fotoğrafa GPS + zaman damgası basılır</small>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={capture} disabled={busy} data-testid="field-camera-input"/>
        </label>
        <textarea className="field-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Saha notu (isteğe bağlı)" data-testid="field-note"/>
        <div className="field-verdicts">
          {VERDICTS.map(({ action, label, cls, icon: Icon }) => (
            <button key={action} className={`field-verdict ${cls}`} onClick={() => verify(action)} data-testid={`field-verdict-${action}`}>
              <Icon size={18}/> {label}
            </button>
          ))}
        </div>
        <div className="field-shots" data-testid="field-shots">
          {shots.map((s) => (
            <a key={s.id} className="field-shot" href={`${API}/evidence/${s.id}/download`} target="_blank" rel="noreferrer" data-testid={`field-shot-${s.id}`}>
              {s.content_type?.startsWith("image/") ? <img src={`${API}/evidence/${s.id}/download`} alt={s.file_name}/> : <span>{s.file_name}</span>}
              {s.geo && <em><MapPin size={10}/> {s.geo.lat.toFixed(4)}, {s.geo.lng.toFixed(4)}</em>}
            </a>
          ))}
          {!shots.length && <small className="evidence-empty">Bu bulgu için henüz saha kanıtı yok.</small>}
        </div>
        {shots[0]?.geo && <a className="field-map-link" href={mapsUrl(shots[0].geo)} target="_blank" rel="noreferrer" data-testid="field-map-link"><MapPin size={12}/> Son kanıtın konumunu haritada aç</a>}
      </div>
    </div>
  );
}
